import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { customersAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { ArrowLeft, Loader2, UserPlus, Check } from 'lucide-react';
import { toast } from 'sonner';

export default function TechNewLead() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    primary_phone: '',
    billing_email: '',
    service_street: '',
    service_city: '',
    service_state: '',
    service_zip: '',
    notes: '',
  });

  const set = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  const canSubmit = form.first_name.trim() && form.primary_phone.trim() && form.service_street.trim();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    try {
      const res = await customersAPI.create({
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim() || null,
        primary_phone: form.primary_phone.trim(),
        billing_email: form.billing_email.trim() || null,
        service_street: form.service_street.trim(),
        service_city: form.service_city.trim() || null,
        service_state: form.service_state.trim() || null,
        service_zip: form.service_zip.trim() || null,
        notes: form.notes.trim() || null,
        customer_type: 'residential',
      });
      toast.success('Lead created');
      navigate('/tech', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create lead');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-slate-50" data-testid="tech-new-lead">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-white border-b border-slate-200">
        <button onClick={() => navigate(-1)} className="p-1.5 -ml-1 rounded-lg active:bg-slate-100" data-testid="tech-new-lead-back">
          <ArrowLeft className="w-5 h-5 text-slate-700" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-base font-bold text-slate-900">New Lead</h1>
          <p className="text-[11px] text-slate-400">Quick-create from the field</p>
        </div>
        <div className="w-9 h-9 rounded-full bg-emerald-50 flex items-center justify-center">
          <UserPlus className="w-4 h-4 text-emerald-600" />
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
        <div className="px-4 py-5 space-y-5">
          {/* Name */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Contact</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-500">First Name *</Label>
                <Input value={form.first_name} onChange={set('first_name')} placeholder="John" className="mt-1 h-10 text-sm bg-slate-50 border-slate-200" data-testid="lead-first-name" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Last Name</Label>
                <Input value={form.last_name} onChange={set('last_name')} placeholder="Doe" className="mt-1 h-10 text-sm bg-slate-50 border-slate-200" data-testid="lead-last-name" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Phone *</Label>
              <Input value={form.primary_phone} onChange={set('primary_phone')} type="tel" placeholder="(555) 123-4567" className="mt-1 h-10 text-sm bg-slate-50 border-slate-200" data-testid="lead-phone" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Email</Label>
              <Input value={form.billing_email} onChange={set('billing_email')} type="email" placeholder="john@example.com" className="mt-1 h-10 text-sm bg-slate-50 border-slate-200" data-testid="lead-email" />
            </div>
          </div>

          {/* Address */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Service Address</p>
            <div>
              <Label className="text-xs text-slate-500">Street *</Label>
              <Input value={form.service_street} onChange={set('service_street')} placeholder="123 Main St" className="mt-1 h-10 text-sm bg-slate-50 border-slate-200" data-testid="lead-street" />
            </div>
            <div className="grid grid-cols-5 gap-2">
              <div className="col-span-2">
                <Label className="text-xs text-slate-500">City</Label>
                <Input value={form.service_city} onChange={set('service_city')} placeholder="City" className="mt-1 h-10 text-sm bg-slate-50 border-slate-200" data-testid="lead-city" />
              </div>
              <div className="col-span-1">
                <Label className="text-xs text-slate-500">State</Label>
                <Input value={form.service_state} onChange={set('service_state')} placeholder="TX" maxLength={2} className="mt-1 h-10 text-sm bg-slate-50 border-slate-200 uppercase" data-testid="lead-state" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs text-slate-500">ZIP</Label>
                <Input value={form.service_zip} onChange={set('service_zip')} placeholder="75001" className="mt-1 h-10 text-sm bg-slate-50 border-slate-200" data-testid="lead-zip" />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Notes</p>
            <Textarea value={form.notes} onChange={set('notes')} placeholder="What does the customer need?" rows={3} className="text-sm bg-slate-50 border-slate-200 resize-none" data-testid="lead-notes" />
          </div>
        </div>

        {/* Sticky submit */}
        <div className="sticky bottom-0 px-4 py-3 bg-white border-t border-slate-200">
          <Button
            type="submit"
            disabled={saving || !canSubmit}
            className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-sm font-bold rounded-xl"
            data-testid="lead-submit-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
            Create Lead
          </Button>
        </div>
      </form>
    </div>
  );
}
